import {
  Section,
  Work,
  WorkAnalysis,
  BaseWork,
  Book
} from "@/app/types/book";
import { BackgroundTask, BackgroundTaskStatus, TaskType } from "@/app/types/tasks";
import { Product, ProductType } from "@/app/types/products";
import { Feedback, FeedbackType } from "@/app/types/feedback";

export default class WorkFactory {
  /**
   * Create a work instance based on its type
   * @param workData - The work data
   * @returns BaseWork - The created work instance
   */
  static workToJSON(workData: Work): BaseWork {
    workData.remote_file_metadata = workData.remote_file_metadata;

    switch (workData.work_type) {
      case "book":
        return new Book(workData);
      // Add more work types here as needed
      // case 'screenplay':
      //   return new Screenplay(workData);
      default:
        return new BaseWork(workData);
    }
  }

  /**
   * Transform analysis data to match our expected format
   * @param row - The analysis data
   * @returns WorkAnalysis - The transformed analysis data
   */
  static analysisToJSON(row: any): WorkAnalysis {
    return {
      id: row.id,
      work_id: row.work_id,
      analysis_category: row.analysis_category,
      analysis_type: row.analysis_type,
      analysis_json: row.analysis_json,
      analysis_md: row.analysis_md || "",
      created_at: row.created_at,
      updated_at: row.updated_at,
      state: row.state,
      metadata: row.metadata,
      remote_file_metadata: row.remote_file_metadata,
      error: row.error,
      score: row.score,
    };
  }

  static sectionToJSON(row: any): Section {
    return {
      id: row.id,
      work_id: row.work_id,
      friendly_id: row.friendly_id,
      title: row.title,
      order_index: row.order_index,
      content: row.content,
      vector_embedding: row.vector_embedding,
      created_at: row.created_at,
      updated_at: row.updated_at,
      remote_file_metadata: row.remote_file_metadata,
    };
  }

  /**
   * Transform task data to match our expected format
   * @param taskData - The task data
   * @returns BackgroundTask - The transformed task data
   */
  static taskToJSON(taskData: any): BackgroundTask {
    if (!taskData) {
      throw new Error("No task data provided");
    }

    const task: BackgroundTask = {
      id: taskData.id,
      type: taskData.type as TaskType,
      status: taskData.status as BackgroundTaskStatus,
      result: taskData.result,
      error: taskData.error,
      parameters: taskData.parameters,
      metadata: taskData.metadata || {},
      retryCount: taskData.retry_count,
      nextRetryAt: taskData.next_retry_at
        ? new Date(taskData.next_retry_at)
        : undefined,
      createdAt: new Date(taskData.created_at),
      updatedAt: new Date(taskData.updated_at),
    };

    return task;
  }

  static productDataToProductObject(productData: any): Product {
    if (!productData) {
      throw new Error("No product data provided");
    }

    const product: Product = {
      id: productData.id,
      name: productData.name,
      description: productData.description,
      price: productData.price,
      product_type: productData.product_type as ProductType,
      metadata: productData.metadata,
      created_at: new Date(productData.created_at),
      updated_at: new Date(productData.updated_at),
      code_name: productData.code_name,
    };

    return product;
  }

  /**
   * Transform feedback data to match our expected format
   * @param row - The feedback data from the database
   * @returns Feedback - The transformed feedback data
   */
  static feedbackToJSON(row: any): Feedback {
    if (!row) {
      throw new Error("No feedback data provided");
    }

    return {
      id: row.id,
      user_id: row.user_id,
      work_id: row.work_id,
      feedback_type: row.feedback_type as FeedbackType,
      feedback_content: row.feedback_content,
      created_at: row.created_at,
      updated_at: row.updated_at,
      metadata: row.metadata || {},
      location_url: row.location_url,
      location_page: row.location_page,
      location_element: row.location_element,
      star_rating: row.star_rating,
      is_issue: row.is_issue,
    };
  }
}

